import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { apiCall } from "../Apis/apiCall";
import { handleApiResponse, handleCustomResponse } from "../utils/response";
import { Icon } from "@iconify/react/dist/iconify.js";
import Shimmer from "../components/ui/Shimmer";
import Breadcrumb from "../components/Breadcrumb";
import { CancelButtonForm, UpdateButtonForm } from "../components/ui/Button";

const EditRolePage = () => {
  const { roleId } = useParams();
  const [roleName, setRoleName] = useState("");
  const [description, setDescription] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [saveLoading, setSaveLoading] = useState(false);
  const [roleLoading, setRoleLoading] = useState(false);
  const navigate = useNavigate();

  const isFormValid = () => {
    return roleName.trim() !== "" && description.trim() !== "";
  };

  const fetchRoleById = async (roleId) => {
    try {
      setRoleLoading(true);
      const response = await apiCall(
        "get",
        `/get-role-by-id/${roleId}`,
        null,
        navigate
      );

      if (response?.data?.success) {
        handleApiResponse(response.data, "success", "GET");
        const roleData = response.data.data || response.data.role || null;
        if (roleData) {
          setRoleName(roleData.role_name || "");
          setDescription(roleData.description || "");
        } else {
          setErrorMessage("Failed to load role details.");
          handleCustomResponse("error", "Failed to load role details.");
        }
      } else {
        setErrorMessage(
          response?.data?.message || "Failed to fetch role details."
        );
        handleCustomResponse("error", "Failed to fetch role details.");
      }
    } catch (error) {
      handleApiResponse(error.response?.data, "Err", "GET");
      setErrorMessage(error.response?.data?.message || "Error fetching role details.");
    } finally {
      setRoleLoading(false);
    }
  };

  useEffect(() => {
    if (roleId && !isNaN(roleId)) {
      fetchRoleById(roleId);
    } else {
      setErrorMessage("Invalid or missing Role ID.");
      handleCustomResponse("error", "Invalid or missing Role ID.");
    }
  }, [roleId]);

  const handleUpdateRole = async (e) => {
    e.preventDefault();

    if (!roleName.trim() || !description.trim()) {
      setErrorMessage("All fields are required.");
      return;
    }

    if (!roleId || isNaN(roleId)) {
      setErrorMessage("Invalid or missing Role ID.");
      return;
    }

    try {
      setSaveLoading(true);
      const response = await apiCall(
        "put",
        `/update-role/${roleId}`,
        { role_name: roleName, description },
        navigate
      );

      if (response?.data?.success) {
        handleApiResponse(response.data, "success", "PUT");
        // setSuccessMessage("Role updated successfully!");
        setErrorMessage("");
        setTimeout(() => {
          navigate("/all-roles");
        }, 1000);
      } else {
        setErrorMessage(response?.data?.message || "Failed to update role.");
        handleCustomResponse("error", "Failed to update role.");
      }
    } catch (error) {
      handleApiResponse(error.response?.data, "Err", "PUT");
      setErrorMessage(error.response?.data?.message || "Error updating role.");
      setSuccessMessage("");
    } finally {
      setSaveLoading(false);
    }
  };

  return (
    <>
      <Breadcrumb title="Edit Role" />
      <div className="card h-100 p-0 radius-12">
        <div className="card-body p-24">
          {errorMessage && (
            <div
              className="alert alert-danger alert-dismissible fade show mb-3"
              role="alert"
            >
              {errorMessage}
              <button
                type="button"
                className="btn-close"
                onClick={() => setErrorMessage("")}
              ></button>
            </div>
          )}
          {successMessage && (
            <div
              className="alert alert-success alert-dismissible fade show mb-3"
              role="alert"
            >
              {successMessage}
              <button
                type="button"
                className="btn-close"
                onClick={() => setSuccessMessage("")}
              ></button>
            </div>
          )}
          {roleLoading ? (
            <Shimmer type="modal" />
          ) : (
            <form onSubmit={handleUpdateRole} autoComplete="off">
              <div className="row g-20">
                <div className="col-md-6 mb-20">
                  <label htmlFor="roleName" className="form-label">
                    Role Name *
                  </label>
                  <input
                    id="roleName"
                    type="text"
                    className="form-control radius-8"
                    value={roleName}
                    onChange={(e) => setRoleName(e.target.value)}
                    placeholder="Enter role name"
                    disabled={saveLoading}
                  />
                </div>
                <div className="col-md-6 mb-20">
                  <label htmlFor="description" className="form-label">
                    Description *
                  </label>
                  <textarea
                    id="description"
                    className="form-control radius-8"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Enter description"
                    rows="3"
                    disabled={saveLoading}
                  />
                </div>
              </div>
              <div className="d-flex justify-content-center gap-3">
                <CancelButtonForm onClick={() => navigate("/all-roles")} />
                <UpdateButtonForm
                  disabled={saveLoading || !isFormValid()}
                  text={saveLoading ? "Please wait..." : "Update Role"}
                />
              </div>
            </form>
          )}
        </div>
      </div>
    </>
  );
};

export default EditRolePage;